/**
 * The controls every CRM form is built out of: a label over a field, a sunken
 * pill holding one input, a single-line row, a chip list and a folded group.
 *
 * The first two lived inside `NewClientModal` until the edit form needed them.
 * Copying them would have been the cheaper edit and the worse one: the add and
 * the edit form have to look like the same form, and two copies drift the first
 * time one of them gets a padding tweak. The last three came out of
 * `ProjectForm` for the same reason - none of them knows anything about a
 * project, and the form was carrying them only because it was where they were
 * first needed.
 *
 * Nothing here reads the translation catalogue. A control that translated its
 * own labels would decide the wording for every screen that draws it, so the
 * caller passes finished text (`removeLabel` on `TagChips` is the only one that
 * is not obvious).
 */
import { useState } from "react";
import { ChevronRight, X } from "lucide-react";

/** A label over a field, the way the design writes forms. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  /** A hairline note under the control, for a field whose empty state means
   *  something ("follow the desk default") rather than nothing. */
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <>
      <label className="crm-lab">{label}</label>
      {children}
      {hint ? <p className="crm-fnote mt-1">{hint}</p> : null}
    </>
  );
}

/** One sunken pill holding a plain text input. */
export function Pill({
  value,
  onChange,
  placeholder,
  type,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: "numeric" | "email" | "tel" | "url";
}) {
  return (
    <label className="crm-inp">
      <input
        type={type ?? "text"}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
      />
    </label>
  );
}

/**
 * Free-text chips you can take off again, for a column that holds a list of
 * plain strings.
 *
 * Deliberately not `LabelChips`: that one resolves ids against the mail and
 * call label registry and draws nothing for an id it does not know, which for a
 * typed-in string would be every chip. Same pill (`crm-chip`), with a remove
 * control on it.
 *
 * Enter and comma commit, Backspace on an empty box takes the last chip back,
 * and so does leaving the field: a value typed and left sitting in the box when
 * the operator reaches for Save would otherwise be dropped without a word.
 *
 * The two ceilings are required props, not defaults. The control draws columns
 * with different limits (project tags take thirty entries of fifty characters,
 * `services` and `features` take fifty of two hundred), and a default here would
 * silently cut a service name at the tag ceiling on its way in - a truncation
 * performed by the field is invisible to everyone afterwards. Pass the number
 * the route states.
 */
export function TagChips({
  tags,
  onChange,
  placeholder,
  maxLen,
  maxCount,
  removeLabel,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  maxLen: number;
  maxCount: number;
  /** Accessible name of the remove button, already translated. */
  removeLabel: string;
}) {
  const [draft, setDraft] = useState("");

  function add(raw: string) {
    const tag = raw.trim().slice(0, maxLen);
    setDraft("");
    if (!tag || tags.length >= maxCount || tags.includes(tag)) return;
    onChange([...tags, tag]);
  }

  return (
    <>
      <label className="crm-inp">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter must not reach the form: adding a chip is not submitting.
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add(draft);
              return;
            }
            if (e.key === "Backspace" && !draft && tags.length) onChange(tags.slice(0, -1));
          }}
          onBlur={() => add(draft)}
          placeholder={placeholder}
          maxLength={maxLen}
          autoComplete="off"
        />
      </label>
      {tags.length ? (
        <span className="mt-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span key={tag} className="crm-chip">
              {tag}
              <button
                type="button"
                className="-mr-1.5 inline-flex rounded-full p-0.5 opacity-70 hover:opacity-100"
                onClick={() => onChange(tags.filter((x) => x !== tag))}
                aria-label={`${removeLabel}: ${tag}`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
}

/** One single-line column. Wrapped in a div of its own so it can sit in a
 *  two-column grid without its label and its box becoming two separate cells. */
export function TextRow({
  label,
  value,
  onChange,
  maxLength,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength: number;
  placeholder?: string;
}) {
  return (
    <div>
      <Field label={label}>
        <label className="crm-inp">
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={maxLength}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
      </Field>
    </div>
  );
}

/**
 * A group of secondary columns a form keeps folded.
 *
 * Ten more boxes on top of the ones a dialog is actually opened for would bury
 * them, so the secondary groups start closed. Closed, not absent: `<details>`
 * keeps every field in the DOM and in the tab order, so find-in-page still lands
 * on the right one and nothing is saved from a box the operator could not see.
 *
 * A group whose columns already carry something starts open instead, because
 * there the fold would be hiding an answer rather than an empty box. That is
 * read ONCE, on mount: recomputing it would slam the group shut under the
 * operator's hands the moment they emptied the last filled field in it.
 *
 * ## The triangle is ours, not the browser's
 *
 * A bare `<summary>` draws the platform's disclosure marker: a solid triangle
 * whose size, colour and spacing come from the operating system, sitting above
 * fields this app drew. It is the same complaint the native `<select>` earned
 * on these forms. So the marker is turned off (`list-none` plus the WebKit
 * pseudo-element, which ignores it) and a `ChevronRight` is drawn instead,
 * rotating a quarter turn when the group opens - the same glyph and the same
 * quarter turn the CRM's menus use.
 *
 * `<details>` is kept rather than swapped for a Radix disclosure, and that is a
 * deliberate stop: the element already gives keyboard operation, the open state
 * and find-in-page reaching a folded field for nothing, and none of those is
 * what looked wrong. Only the triangle was.
 */
export function FoldGroup({
  title,
  openWhen,
  children,
}: {
  title: string;
  openWhen: boolean;
  children: React.ReactNode;
}) {
  const [initiallyOpen] = useState(openWhen);
  return (
    <details open={initiallyOpen} className="group mt-6 border-t border-[var(--crm-hair)] pt-3.5">
      <summary className="flex cursor-pointer select-none list-none items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden />
        {title}
      </summary>
      {children}
    </details>
  );
}
